import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Image as ImageIcon, Settings as SettingsIcon, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    sections: 0,
    pages: 0,
    media: 0,
  });
  const [recentSections, setRecentSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('cms_token');
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const [sectionsRes, pagesRes, mediaRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/sections`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/pages`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/media`, { headers }),
      ]);

      const sections = await sectionsRes.json();
      const pages = await pagesRes.json();
      const media = await mediaRes.json();

      setStats({
        sections: sections.length,
        pages: pages.length,
        media: media.length,
      });

      setRecentSections(sections.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      icon: <FileText size={32} />,
      label: 'Total Sections',
      value: stats.sections,
      color: 'purple',
      link: '/admin/sections',
    },
    {
      icon: <ImageIcon size={32} />,
      label: 'Media Files',
      value: stats.media,
      color: 'blue',
      link: '/admin/media',
    },
    {
      icon: <SettingsIcon size={32} />,
      label: 'Pages',
      value: stats.pages,
      color: 'green',
      link: '/admin/sections',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-purple-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-black mb-2">Dashboard</h1>
        <p className="text-gray-400">Welcome to your CMS dashboard</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statCards.map((card, index) => (
          <Link key={index} to={card.link}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`text-${card.color}-500`}>{card.icon}</div>
                <TrendingUp className="text-green-500" size={20} />
              </div>
              <div className="text-4xl font-black mb-2">{card.value}</div>
              <div className="text-sm text-gray-400">{card.label}</div>
            </motion.div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/admin/sections"
              data-testid="quick-add-section"
              className="block bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-bold transition-all"
            >
              + Add New Section
            </Link>
            <Link
              to="/admin/media"
              data-testid="quick-upload-media"
              className="block bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg font-bold transition-all"
            >
              + Upload Media
            </Link>
            <Link
              to="/admin/settings"
              data-testid="quick-edit-settings"
              className="block bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-lg font-bold transition-all"
            >
              Edit Site Settings
            </Link>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-bold mb-4">Recent Sections</h2>
          {recentSections.length === 0 ? (
            <p className="text-gray-400 text-sm">No sections yet. Create your first section!</p>
          ) : (
            <div className="space-y-3">
              {recentSections.map((section) => (
                <Link
                  key={section.id}
                  to={`/admin/sections`}
                  className="block bg-white/5 hover:bg-white/10 p-3 rounded-lg transition-all"
                >
                  <div className="font-bold text-sm">{section.heading || 'Untitled Section'}</div>
                  <div className="text-xs text-gray-400 mt-1">{section.section_key}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;